import { useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatDistanceToNowStrict } from "date-fns";
import { mutate } from "swr";
import * as yup from "yup";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { useFetcher } from "~/hooks/useFetcher";
import { usePrompt } from "~/hooks/usePrompt";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  OverviewTile,
  OverviewTiles,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import {
  Agent,
  AgentAccessLevel,
  AgentScope,
  SelectableModel,
  agentPath,
  modelsPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import { AgentAccessBadge } from "./AgentAccessBadge";
import { AgentAvatar } from "./AgentAvatar";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

// Bumps the default Label size/weight so Model/Description/Persona/Instructions
// read as section headers rather than blending into the field content.
const FIELD_LABEL_CLASSNAME = "text-lg font-semibold";

// Lets the Save button (rendered in the header) submit this form by id.
const FORM_ID = "agent-profile-form";

const schema = yup.object({
  description: yup.string().defined(),
  persona: yup.string().defined(),
  instructions: yup.string().defined(),
  model: yup.string().defined(),
});

type FormValues = yup.InferType<typeof schema>;

interface MarkdownFieldProps {
  name: "persona" | "instructions";
  label: string;
  control: Control<FormValues>;
  isEditable: boolean;
  isSaving: boolean;
}

// Viewers get the rendered markdown, borderless, matching how the Dataset and
// Project overviews present read-only text.
const MarkdownField = ({ name, label, control, isEditable, isSaving }: MarkdownFieldProps) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={name} className={FIELD_LABEL_CLASSNAME}>
          {label}
        </Label>
        {isEditable ? (
          <MarkdownEditor
            id={name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            readOnly={isSaving}
            paneClassName="min-h-[15rem] max-h-[36rem] overflow-y-auto"
          />
        ) : (
          <MarkdownPreview>{field.value}</MarkdownPreview>
        )}
      </div>
    )}
  />
);

interface AgentProfileTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const AgentProfileTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentProfileTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const isEditable = !sessionSidebarView && canEditGrant;
  const isSystemAgent = agent.scope === AgentScope.SYSTEM;
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const { collaborators, accessLevels } = useAgentShare(agent.id);
  const { isFavorite, toggleFavorite } = useFavorites("agent-favorites", [agent], (a) => a.id);

  const { data: models } = useFetcher<SelectableModel[]>(modelsPath());
  // Always keep the agent's current model selectable, even if it is not in the
  // catalog (e.g. a value set via the API that predates the curated picker).
  const modelOptions: SelectableModel[] =
    models && !models.some((m) => m.name === agent.model)
      ? [{ name: agent.model, display_name: agent.model }, ...models]
      : (models ?? [{ name: agent.model, display_name: agent.model }]);

  const owners = useOwners(collaborators, accessLevels, AgentAccessLevel.OWNER);

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      // `access_level` is the caller's resolved level — at least USER for a SYSTEM
      // agent via its `user:*` grant, higher for a directly-granted collaborator.
      content: <AgentAccessBadge accessLevel={agent.access_level} />,
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} />,
    },
    {
      label: "Type",
      content: isSystemAgent ? "System" : "Custom",
    },
    {
      label: "Last Updated",
      content: agent.updated_at
        ? formatDistanceToNowStrict(new Date(agent.updated_at), {
            addSuffix: true,
          })
        : "—",
    },
  ];

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      description: agent.description ?? "",
      persona: agent.persona ?? "",
      instructions: agent.instructions ?? "",
      model: agent.model ?? "",
    },
  });

  // Warn before navigating away (tab switch, browser close/refresh) with unsaved edits.
  usePrompt(isDirty ? UNSAVED_CHANGES_MESSAGE : false, { beforeUnload: true });

  const doSave = async (values: FormValues) => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, {
        description: values.description,
        persona: values.persona,
        instructions: values.instructions,
        model: values.model,
      });
      reset(values);
      // `description` shows on the list card, so refresh the lists too.
      await Promise.all([mutate(agentPath(agent.id)), mutateAgentList()]);
      showSuccess("Agent saved");
    } catch {
      showError("Failed to save agent");
    } finally {
      setIsSaving(false);
    }
  };

  // Name edits commit immediately (inline, separate from the form's Save button).
  const handleRename = async (next: string) => {
    try {
      await updateAgent(agent.id, { display_name: next });
      // Name drives the list label and its ordering, so refresh the lists too.
      await Promise.all([mutate(agentPath(agent.id)), mutateAgentList()]);
    } catch (e) {
      showError("Failed to rename agent");
      throw e;
    }
  };

  // Matches the borderless look Dataset/Project overviews use for read-only
  // text — otherwise the disabled shadcn inputs still render a visible box.
  const readOnlyFieldClassName = !isEditable
    ? "min-h-0 border-transparent bg-transparent p-0 shadow-none disabled:cursor-text disabled:opacity-100 [&_svg]:hidden"
    : undefined;

  const saveAction = isEditable ? (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => reset()} disabled={!isDirty || isSaving}>
        Discard
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        size="sm"
        disabled={!isDirty || !isValid || isSaving}
        loading={isSaving}
      >
        Save
      </Button>
    </div>
  ) : undefined;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      {!sessionSidebarView && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgentAvatar agent={agent} size={48} />
            <EditableTitle
              value={agent.display_name}
              canEdit={isEditable}
              label="Agent name"
              onSave={handleRename}
            />
            <FavoriteButton
              favorite={isFavorite(agent.id)}
              onToggle={() => toggleFavorite(agent.id)}
            />
          </div>
          {saveAction}
        </div>
      )}

      <OverviewTiles tiles={tiles} />

      <form id={FORM_ID} onSubmit={handleSubmit(doSave)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="model" className={FIELD_LABEL_CLASSNAME}>
            Model
          </Label>
          {isEditable ? (
            <Controller
              control={control}
              name="model"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isSaving}>
                  <SelectTrigger id="model" aria-label="Model">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((m) => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          ) : (
            <p id="model" className="text-sm text-foreground">
              {modelOptions.find((m) => m.name === agent.model)?.display_name ?? agent.model}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="description" className={FIELD_LABEL_CLASSNAME}>
            Description
          </Label>
          <Textarea
            id="description"
            rows={3}
            disabled={!isEditable || isSaving}
            className={readOnlyFieldClassName}
            {...register("description")}
          />
        </div>

        {(agent.persona || isEditable) && (
          <MarkdownField
            name="persona"
            label="Persona"
            control={control}
            isEditable={isEditable}
            isSaving={isSaving}
          />
        )}

        {(agent.instructions || isEditable) && (
          <MarkdownField
            name="instructions"
            label="Instructions"
            control={control}
            isEditable={isEditable}
            isSaving={isSaving}
          />
        )}
      </form>
    </div>
  );
};
